import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';
import { useDashboardFilters } from '@/contexts/DashboardFilterContext';
import dayjs from 'dayjs';
import {
  Card, Typography, Box, Grid, LinearProgress,
  CircularProgress, Alert
} from '@mui/material';
import { buildFilterBody, PLATFORM_COLORS } from './utils';
import axiosInstance from "@/utils/axios";
import { SUPPORTED_SPORTS } from 'constants/globalConstants';

// ─── Tipi e Interfacce ────────────────────────────────────────────────────────

interface PlatformConfigItem {
  [x: string]: any;
  name: string;
  color: string;
  sharePercent: number;
  clips: number;
  goalParade: number;
  highlights: number;
}

interface SocialPlatformData {
  [x: string]: any;  
  platform: string;
  value: number;
  color: string;
  total: number;
  clips: number;
  goalParade: number;
  highlights: number;
}

// ─── Utility: calcola i dati integrati dalla risposta API ─────────────────────
function buildIntegratedData(
  apiData: PlatformConfigItem[],
  dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null | undefined
): SocialPlatformData[] {
  return apiData.map((platform) => {
    const calculated = platform.sharePercent * 100;
    return {
      platform:     platform.name,
      value:        Number(calculated.toFixed(2)),
      color:        PLATFORM_COLORS[platform.name],
      total:        platform.total,
      clips:        platform.clips,
      goalParade:   platform.goalParade,
      highlights:   platform.highlights,
    };
  });
}

// ─── Sub-Componente: Barra di Progresso per i Formati Video ──────────────────

interface MiniProgressStatProps {
  label: string;
  value: number;
  percentage: number;
  color: string;
}
const CURRENT_SPORT = ((import.meta as any).env.VITE_APP_SPORT).toUpperCase();

const MiniProgressStat = ({ label, value, percentage, color }: MiniProgressStatProps) => (
  <Box sx={{ width: '100%', mb: 0.75 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.25 }}>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 500 }}>
        # {label} : {value}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.primary', fontWeight: 700 }}>
        {percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(2)}%
      </Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={percentage}
      sx={{
        height: 5,
        borderRadius: 4,
        backgroundColor: 'action.hover',
        '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 4 },
      }}
    />
  </Box>
);

// ─── Componente Principale ────────────────────────────────────────────────────

export default function SocialSharesPlatformChart() {
  const chartRef     = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Dati pronti per UI e chart: derivati dalla risposta API + dateRange
  const [chartData, setChartData] = useState<SocialPlatformData[]>([]);

  const { contentType, competition, season, dateRange, updateDashboard, filterLoaded} = useDashboardFilters();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);

  // ── 1. Fetch dati API ──────────────────────────────────────────────────────
  useEffect(() => {
    if(filterLoaded){
      let cancelled = false;

      const fetchData = async () => {      
        try {
          setIsLoading(true);
          setError(null);

          const body = buildFilterBody(
            contentType  ?? [],
            competition  ?? [],
            season       ?? [],
            dateRange
          );

          // FIX: await mancante nell'originale
          const res = await axiosInstance.post<PlatformConfigItem[]>(
            '/dashboard/getSocialPlatformData',
            body
          );

          if (!cancelled) {
            // FIX: integrazione calcolata qui, non nel chart effect → nessun loop
            setChartData(buildIntegratedData(res.data, dateRange));
          }
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to load analytics data');
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      };

      fetchData();
      return () => { cancelled = true; };
    }
  // eslint-disable-line react-hooks/exhaustive-deps
  }, [updateDashboard, filterLoaded]);

  // ── 2. Inizializza / aggiorna il grafico ECharts ──────────────────────────
  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return;

    const chartInstance = echarts.init(chartRef.current);
    const totalShares   = chartData.reduce((sum, d) => sum + d.total, 0);

    const option: echarts.EChartsOption = {
      animationDuration: 800,
      tooltip: {
        trigger: 'item',
        formatter: '{b}: <b>{c}</b> shares ({d}%)',
      },
      legend: {
        orient: 'vertical',
        right: '5%',
        top: 'center',
        textStyle: { 
          color: '#ffffff',
        },
        inactiveColor: '#666666',
        formatter: (name: string) => {
          const item = chartData.find((d) => d.platform === name);
          return name;
        },
      },
      title: {
        text:      totalShares.toLocaleString(),
        subtext:   'Total Shares',
        left:      '34%',
        top:       '43%',
        textAlign: 'center',
        textStyle:    { fontSize: 24, fontWeight: 'bold', color: '#ffffff' },
        subtextStyle: { fontSize: 12, color: '#9ca3af', fontWeight: 500 },
      },
      series: [
        {
          name:  'Social Platform Shares',
          type:  'pie',
          radius: ['55%', '75%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 0 },
          label:     { show: false },
          emphasis:  { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.15)' } },
          data: chartData.map((d) => ({
            value:     d.total,
            name:      d.platform,
            itemStyle: { color: d.color },
          })),
        },
      ],
    };

    chartInstance.setOption(option, true);

    // ResizeObserver sul container Grid
    const ro = new ResizeObserver(() => chartInstance.resize());
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', () => chartInstance.resize());

    return () => {
      window.removeEventListener('resize', () => chartInstance.resize());
      ro.disconnect();
      chartInstance.dispose();
    };
  }, [chartData]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }} data-tour={"dashboard_social_media_distribution"}>

      {/* Header */}
      <Box px={3} pt={2.5} pb={2}>
        <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ fontSize: '1.25rem' }}>
          Social Media Content Distribution
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Share of generated content and breakdown by format across social channels
        </Typography>
      </Box>

      {/* ── Loading ── */}
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={380}>
          <CircularProgress size={40} />
        </Box>
      )}

      {/* ── Error ── */}
      {!isLoading && error && (
        <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1} px={3}>
          <Typography variant="body2" color="error" sx={{ fontWeight: 500, mb:10 }}>
            {error}
          </Typography>
        </Box>
      )}

      {/* ── Content ── */}
      {!isLoading && !error && 
        (!chartData || chartData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={380}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              No data available to display
            </Typography>
          </Box>
        ) : (
          <Grid container ref={containerRef}>

            {/* Grafico Donut */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '380px' }} />
            </Grid>

            {/* Lista piattaforme */}
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{
                px: 3,
                bgcolor: 'background.paper',
                borderLeft:  { md: '1px solid' },
                borderColor: { md: 'divider' },
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              <Box display="flex" flexDirection="column" gap={2}>
                {chartData.map((social) => (
                  <Box
                    key={social.platform}
                    sx={{
                      p: 1.5,
                      borderRadius: '8px',
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'action.hover',
                    }}
                  >
                    {/* Header riga */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: social.color }} />
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: 'text.primary' }}>
                          {social.platform}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary' }}>
                        Vol: {social.total}
                      </Typography>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid size={{ xs: 4 }}>
                        <MiniProgressStat label="Clip" value={social.clips} percentage={(social.clips / social.total)*100 } color={social.color} />
                      </Grid>
                      {CURRENT_SPORT === SUPPORTED_SPORTS.FOOTBALL && <Grid size={{ xs: 4 }}>
                        <MiniProgressStat label="Goal Parade" value={social.goalParade} percentage={(social.goalParade / social.total)*100 } color={social.color} />
                      </Grid>}
                      <Grid size={{ xs: 4 }}>
                        <MiniProgressStat label="Highlight" value={social.highlights} percentage={(social.highlights / social.total)*100 } color={social.color} />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Grid>

          </Grid>
        )
      )}

    </Card>
  );
}